import React, { useContext, useEffect, useState } from "react";
import "./friendsScreen.css";
import { DataLayerContext } from "../dataLayer/DataLayer";
import { AppBar } from "./components/appBar";
import { PageHeading } from "./components/pageHeading";
import { Share } from "./components/icons";
import { useObservableProperty } from "../observable/useObservableProperty";
import type { FriendActionResult } from "../dataLayer/DataLayer";

/** Turn an action result into a short user-facing message. */
function actionMessage(result: FriendActionResult, success: string): string {
  switch (result) {
    case "Success":
      return success;
    case "NotSignedIn":
      return "Please sign in again to do that.";
    case "NotFound":
      return "That invite could not be found.";
    case "Expired":
      return "That invite has expired.";
    case "AlreadyUsed":
      return "That invite was already used.";
    case "RateLimited":
      return "You're doing that too quickly — try again in a moment.";
    default:
      return "Something went wrong. Please try again.";
  }
}

/** Copy text to the clipboard, resolving to whether it succeeded. */
async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

type ShareOutcome = "shared" | "copied" | "cancelled" | "failed";

/**
 * Hand an invite link to the OS share sheet, falling back to the clipboard.
 *
 * Web Share (`navigator.share`) opens the native sheet on iOS (including the
 * WKWebView app), Android, and desktop Chrome/Edge — the common path. Browsers
 * without it (desktop Firefox, older Safari) copy the link instead. If the
 * sheet is cancelled we report "cancelled" so the caller leaves the form alone;
 * any other failure (e.g. iOS dropping the user-gesture across the network
 * round-trip) falls back to a copy. If both fail, the caller keeps the link
 * ready for another tap and reports the failure.
 */
async function shareInviteUrl(url: string): Promise<ShareOutcome> {
  if (typeof navigator !== "undefined" && navigator.share) {
    try {
      await navigator.share({ title: "Wirder", text: "Be my friend on Wirder", url });
      return "shared";
    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") {
        return "cancelled";
      }
      const ok = await copyToClipboard(url);
      return ok ? "copied" : "failed";
    }
  }
  const ok = await copyToClipboard(url);
  return ok ? "copied" : "failed";
}

export function FriendsScreen() {
  const dataLayer = useContext(DataLayerContext);
  const roster = useObservableProperty(dataLayer.roster);
  const rosterLoading = useObservableProperty(dataLayer.rosterLoading);
  const pendingInvite = useObservableProperty(dataLayer.pendingInvite);

  // Invite form: "From" is your name (saved as the default others see); "To" is
  // your private label for the person you're inviting.
  const [fromDraft, setFromDraft] = useState("");
  const [toDraft, setToDraft] = useState("");
  const [sharingInvite, setSharingInvite] = useState(false);
  const [inviteStatus, setInviteStatus] = useState<string>("");
  const [preparedInvite, setPreparedInvite] = useState<{
    playerId: string | undefined;
    from: string;
    to: string;
    url: string;
  } | null>(null);

  const [busyFriend, setBusyFriend] = useState<string | null>(null);

  // Two-step confirmation for removing a friend. Holds the id awaiting confirm.
  const [confirmFriendId, setConfirmFriendId] = useState<string | null>(null);

  // Accept flow: the name you'll see for the inviter (pre-filled from their "From").
  const [acceptName, setAcceptName] = useState("");
  const [acceptStatus, setAcceptStatus] = useState<string>("");
  const [accepting, setAccepting] = useState(false);

  // Inline rename of an existing friend.
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState("");
  const [savingRename, setSavingRename] = useState(false);

  // Load the roster on first mount if we don't have it yet.
  useEffect(() => {
    if (!roster && !rosterLoading) {
      void dataLayer.loadRoster();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Seed the "From" field once the roster arrives (only if the user hasn't typed).
  useEffect(() => {
    if (roster && fromDraft === "") {
      setFromDraft(roster.self.displayName ?? "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roster]);

  // Seed the accept-name field from the invite preview when a pending invite
  // appears (only if the user hasn't typed their own).
  useEffect(() => {
    if (pendingInvite?.preview?.fromDisplayName && acceptName === "") {
      setAcceptName(pendingInvite.preview.fromDisplayName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingInvite]);

  const onBack = () => dataLayer.navigateBack();

  // Reuse a prepared link on retry so iOS sharing can run directly from the tap,
  // without another network round-trip consuming the user gesture.
  const onShareInvite = async () => {
    const from = fromDraft.trim();
    const to = toDraft.trim();
    if (sharingInvite || !from || !to) return;
    const playerId = dataLayer.selfPlayerId;
    setSharingInvite(true);
    setInviteStatus("");
    try {
      let url =
        preparedInvite?.playerId === playerId &&
        preparedInvite?.from === from &&
        preparedInvite.to === to
          ? preparedInvite.url
          : null;
      if (!url) {
        const res = await dataLayer.createFriendInvite(from, to);
        if (res.result !== "Success" || !res.url) {
          setInviteStatus(
            res.result === "Error" || res.result === "Success"
              ? "Couldn't create an invite. Check your connection and try again."
              : actionMessage(res.result, ""),
          );
          return;
        }
        url = res.url;
        setPreparedInvite({ playerId, from, to, url });
      }
      const outcome = await shareInviteUrl(url);
      if (outcome === "failed") {
        setInviteStatus("Couldn't open sharing or copy the link. Tap Share invite to try again.");
      } else if (outcome === "cancelled") {
        setInviteStatus("Sharing cancelled. Tap Share invite to try again.");
      } else {
        setToDraft("");
        setPreparedInvite(null);
        setInviteStatus(
          outcome === "copied" ? `Link for ${to} copied \u2014 paste it to them.` : "",
        );
      }
    } catch (err) {
      console.error("Sharing friend invite failed:", err);
      setInviteStatus("Couldn't share an invite. Check your connection and try again.");
    } finally {
      setSharingInvite(false);
    }
  };

  const onRemoveFriend = async (friendId: string) => {
    setBusyFriend(friendId);
    await dataLayer.removeFriendById(friendId);
    setBusyFriend(null);
    setConfirmFriendId(null);
  };

  const startRename = (friendId: string, current: string) => {
    setConfirmFriendId(null);
    setRenamingId(friendId);
    setRenameDraft(current);
  };

  const cancelRename = () => {
    setRenamingId(null);
    setRenameDraft("");
  };

  const onSaveRename = async (friendId: string) => {
    const name = renameDraft.trim();
    if (!name) return;
    setSavingRename(true);
    await dataLayer.renameFriendById(friendId, name);
    setSavingRename(false);
    setRenamingId(null);
    setRenameDraft("");
  };

  const onAcceptPending = async () => {
    if (!pendingInvite) return;
    const name = acceptName.trim();
    if (!name) return;
    setAccepting(true);
    setAcceptStatus("");
    const result = await dataLayer.acceptFriendInvite(pendingInvite.code, name);
    if (result === "Success") {
      setAcceptStatus("");
      setAcceptName("");
    } else {
      setAcceptStatus(actionMessage(result, ""));
    }
    setAccepting(false);
  };

  const onDismissPending = () => {
    dataLayer.clearPendingInvite();
    setAcceptStatus("");
    setAcceptName("");
  };

  // You can't befriend yourself: the backend rejects accepting your own invite.
  // Detect that case up front (the code is one of your own outstanding invites)
  // so we show an explanation instead of an Accept button that would only fail.
  const isOwnInvite = Boolean(
    pendingInvite && roster?.outgoing?.some((o) => o.code === pendingInvite.code),
  );

  const shareDisabled = sharingInvite || !fromDraft.trim() || !toDraft.trim();

  return (
    <div className="screen friends">
      <AppBar onBack={onBack} />
      <PageHeading
        eyebrow="Friends"
        title="Your circle"
        sub="Invite friends to compare scores. Adding a friend has to go both ways."
      />

      <div className="friends__body">
        {pendingInvite && (
          <section className="fcard fcard--accent">
            {isOwnInvite ? (
              <>
                <div className="fcard__title">This is your own invite link</div>
                <p className="fcard__sub">
                  You can&apos;t add yourself as a friend. Share this link with someone else and
                  have <em>them</em> open it to connect.
                </p>
                <div className="frow__actions">
                  <button className="btn btn--quiet" onClick={onDismissPending}>
                    Dismiss
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="fcard__title">
                  {pendingInvite.preview
                    ? `${pendingInvite.preview.fromDisplayName} invited you`
                    : "You have a friend invite"}
                </div>
                <p className="fcard__sub">
                  Accepting lets both of you share daily scores. Viewing rankings and scores on your
                  own leaderboard requires backer access.
                </p>
                <div className="finvite-form">
                  <label className="ffield">
                    <span className="ffield__label">Their name</span>
                    <span className="ffield__hint">
                      The name you&apos;ll see for this person on your leaderboard.
                    </span>
                    <input
                      className="finput"
                      type="text"
                      value={acceptName}
                      maxLength={30}
                      placeholder="Their name"
                      onChange={(e) => setAcceptName(e.target.value)}
                    />
                  </label>
                  <div className="frow__actions">
                    <button
                      className="btn btn--primary"
                      onClick={onAcceptPending}
                      disabled={accepting || !acceptName.trim()}
                    >
                      {accepting ? "Accepting\u2026" : "Accept"}
                    </button>
                    <button
                      className="btn btn--quiet"
                      onClick={onDismissPending}
                      disabled={accepting}
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
                {acceptStatus && <div className="fstatus">{acceptStatus}</div>}
              </>
            )}
          </section>
        )}

        <section className="fcard">
          <div className="fcard__title">Invite a friend</div>
          <p className="fcard__sub">
            Fill in both names, then share a private invite link. Each link works once.
          </p>
          <div className="finvite-form">
            <label className="ffield">
              <span className="ffield__label">Your name</span>
              <span className="ffield__hint">What this friend will see for you.</span>
              <input
                className="finput"
                type="text"
                value={fromDraft}
                maxLength={30}
                placeholder="Your name"
                onChange={(e) => setFromDraft(e.target.value)}
              />
            </label>
            <label className="ffield">
              <span className="ffield__label">Their name</span>
              <span className="ffield__hint">
                The name you&apos;ll see for this person on your leaderboard.
              </span>
              <input
                className="finput"
                type="text"
                value={toDraft}
                maxLength={30}
                placeholder="e.g. Jessie"
                onChange={(e) => setToDraft(e.target.value)}
              />
            </label>
            <button className="btn btn--primary" onClick={onShareInvite} disabled={shareDisabled}>
              <Share className="btn__icon" />
              {sharingInvite ? "Sharing\u2026" : "Share invite"}
            </button>
          </div>
          {inviteStatus && (
            <div className="fstatus" role="status">
              {inviteStatus}
            </div>
          )}
        </section>

        <section className="fcard">
          <div className="fcard__title">
            Friends {roster && roster.friends.length > 0 ? `(${roster.friends.length})` : ""}
          </div>
          {rosterLoading && !roster && <div className="fstatus">{"Loading\u2026"}</div>}
          {roster && roster.friends.length === 0 && (
            <p className="fcard__sub">No friends yet. Send an invite to get started.</p>
          )}
          {roster && roster.friends.length > 0 && (
            <ul className="flist">
              {roster.friends.map((f) =>
                renamingId === f.playerId ? (
                  <li className="frow" key={f.playerId}>
                    <div className="frow__main finput-row">
                      <input
                        className="finput"
                        type="text"
                        value={renameDraft}
                        maxLength={30}
                        placeholder="Their name"
                        autoFocus
                        onChange={(e) => setRenameDraft(e.target.value)}
                      />
                      <button
                        className="btn btn--sm btn--primary"
                        onClick={() => onSaveRename(f.playerId)}
                        disabled={savingRename || !renameDraft.trim()}
                      >
                        {savingRename ? "\u2026" : "Save"}
                      </button>
                      <button
                        className="btn btn--sm btn--quiet"
                        onClick={cancelRename}
                        disabled={savingRename}
                      >
                        Cancel
                      </button>
                    </div>
                  </li>
                ) : (
                  <li className="frow" key={f.playerId}>
                    <div className="frow__main">
                      <div className="frow__name">{f.displayName}</div>
                    </div>
                    <div className="frow__actions">
                      {confirmFriendId === f.playerId ? (
                        <>
                          <button
                            className="btn btn--sm btn--danger"
                            onClick={() => onRemoveFriend(f.playerId)}
                            disabled={busyFriend === f.playerId}
                          >
                            {busyFriend === f.playerId ? "\u2026" : "Remove"}
                          </button>
                          <button
                            className="btn btn--sm btn--quiet"
                            onClick={() => setConfirmFriendId(null)}
                            disabled={busyFriend === f.playerId}
                          >
                            Keep
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            className="btn btn--sm btn--soft"
                            onClick={() => startRename(f.playerId, f.displayName)}
                          >
                            Rename
                          </button>
                          <button
                            className="btn btn--sm btn--quiet"
                            onClick={() => setConfirmFriendId(f.playerId)}
                          >
                            Remove
                          </button>
                        </>
                      )}
                    </div>
                  </li>
                ),
              )}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
